import React, { useEffect, useState, useCallback } from "react";
import ListSidebar from "../components/lists/ListSidebar";
import TaskContentArea from "../components/tasks/TaskContentArea";
import PriorityMatrixPanel from "../components/tasks/PriorityMatrixPanel";
import TaskListModalFABs from "@/components/tasks/modal/TaskListModalFABs";
import { useLists } from "@/context/ListContext";
import { useTasks } from "@/context/TaskContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Kanban, LayoutGrid, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { TaskCreateModal } from "../components/tasks/TaskCreateModal";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const TasksPage: React.FC = () => {
  const { activeListId, lists } = useLists();
  const { fetchTasks, loading, activeTask, addTask } = useTasks();
  const { user } = useAuth();
  const [switching, setSwitching] = useState(false);
  const [lastListId, setLastListId] = useState<string | null>(null);
  const [showEisenhowerMatrix, setShowEisenhowerMatrix] = useState(false);

  // State for the 'create task' modal
  const [showCreateModal, setShowCreateModal] = useState(false);

  // For navigation
  const navigate = useNavigate();

  // New: handle FAB create & focus actions
  const handleCreateTask = useCallback(() => {
    setShowCreateModal(true);
  }, []);
  const handleStartFocus = useCallback(() => {
    // Trigger focus timer or modal logic
    const evt = new CustomEvent("start-focus-timer");
    window.dispatchEvent(evt);
  }, []);

  // Save new task
  const handleSaveTask = (title: string, detail: string) => {
    addTask(title, detail);
  };

  // Handle list switch transitions for smoother UX
  useEffect(() => {
    if (activeListId && activeListId !== lastListId) {
      setSwitching(true);
      fetchTasks(activeListId).finally(() => {
        setTimeout(() => {
          setSwitching(false);
          setLastListId(activeListId);
        }, 250);
      });
    }
  }, [activeListId]); // Only when activeListId changes

  // Find active list's name
  const activeList = lists.find((l) => l.id === activeListId);
  const activeListName = activeList ? activeList.name : "";

  return (
    <div className="flex h-[100vh] bg-background">
      {/* Sidebar */}
      <div
        className={`
          w-full
          max-w-[98vw]
          min-w-0
          sm:w-72 sm:max-w-[340px] sm:min-w-[44vw]
          md:w-96 md:max-w-[400px] md:min-w-[340px]
          lg:w-[420px] lg:max-w-[440px] lg:min-w-[420px]
          bg-white border-r dark:bg-gray-900 dark:border-gray-800 flex flex-col
          transition-all duration-200
          shadow-lg shadow-gray-400/40 dark:shadow-black/40
          z-20
        `}
        style={{
          boxSizing: "border-box"
        }}
      >
        <ListSidebar switching={switching} activeListId={activeListId} />
      </div>
      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0 bg-muted/20 relative">
        <div className="flex flex-col gap-0 pt-6 pb-2 px-6 relative">
          {/* Close button absolute top right */}
          <button
            type="button"
            className="absolute top-3 right-3 z-20 rounded-full p-2 text-muted-foreground hover:bg-gray-100 hover:text-red-500 focus:outline-none dark:hover:bg-gray-800/60 transition-colors"
            onClick={() => navigate("/")}
            aria-label="Close"
            tabIndex={0}
          >
            <X size={32} className="w-8 h-8" />
          </button>
          {/* Main heading (active list name, top left) */}
          <div>
            {/* Match styling to sidebar Lists label */}
            <span className="font-bold text-2xl text-gray-900 dark:text-white font-lora">
              {activeListName || ""}
            </span>
          </div>
          {/* Kanban/Matrix toggle */}
          <div className="flex w-full items-center justify-start mt-3 mb-1">
            <Tabs value={showEisenhowerMatrix ? "matrix" : "kanban"} onValueChange={v => setShowEisenhowerMatrix(v === "matrix")} className="w-auto">
              <TabsList className="rounded-xl bg-gray-100 dark:bg-gray-800 flex gap-2 px-1 py-0.5 shadow-none">
                <TabsTrigger 
                  value="kanban" 
                  className="flex items-center gap-2 px-6 py-2 font-semibold text-base rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-gray-900 data-[state=active]:text-gray-900 dark:data-[state=active]:text-white"
                  aria-label="Kanban Board"
                >
                  <Kanban className="w-5 h-5" />
                  Kanban
                </TabsTrigger>
                <TabsTrigger 
                  value="matrix" 
                  className="flex items-center gap-2 px-6 py-2 font-semibold text-base rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-gray-900 data-[state=active]:text-gray-900 dark:data-[state=active]:text-white"
                  aria-label="Eisenhower Matrix"
                >
                  <LayoutGrid className="w-5 h-5" />
                  Matrix
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
        <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">
          {showEisenhowerMatrix
            ? <PriorityMatrixPanel />
            : (
              <TaskContentArea 
                switching={switching} 
                // No prop needed: now TaskContentArea wires up selection and drag-n-drop
              />
            )}
        </div>
        {/* Floating Action Buttons */}
        <TaskListModalFABs
          isOpen={true}
          isAuthenticated={!!user}
          activeListId={activeListId}
          activeTask={activeTask}
          mainTab="tasks"
          showPriorityMatrix={showEisenhowerMatrix}
          onCreateTask={handleCreateTask}
          onStartFocus={handleStartFocus}
        />
        <TaskCreateModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSave={handleSaveTask}
        />
      </div>
    </div>
  );
};

export default TasksPage;
